import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { environment } from 'environments/environment';
import { AuthService } from 'app/services/auth.service';
import { OffersService } from 'app/services/offers.service';
import { Router } from '@angular/router';
import { ToastsManager } from 'ng2-toastr';
import { Status } from 'app/interfaces/status';
import { StatusService } from 'app/services/status.service';
import { OfferStatusCodesEnum } from 'app/enums/offerStatusCodes.enum';
import { Subscription } from 'rxjs/Subscription';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ng2-cookies';

@Component({
  selector: 'app-offer-upload',
  templateUrl: './offer-upload.component.html',
  styleUrls: ['./offer-upload.component.scss']
})
export class OfferUploadComponent implements OnInit, OnDestroy {
  @ViewChild('myPond')
  myPond: any;
  @ViewChild('tagsChipList')
  tagChipListEl: any;

  public filePondOption: {};
  public body: any;
  public dryRun = true;
  public decoding = true;
  public statusCode: string = OfferStatusCodesEnum.NEW;
  public offerLifeCycleStatus: Status[];
  public customTags: string[] = [];
  public fileIsLoaded = false;
  private subscriptions: Subscription = new Subscription();

  constructor(
    public authService: AuthService,
    private offerService: OffersService,
    private router: Router,
    private toastr: ToastsManager,
    private statusService: StatusService,
    private cookieService: CookieService,
    public translate: TranslateService
  ) {
    this.filePondOption = this.getUploadConfig();
  }

  ngOnInit() {
    this.subscriptions.add(
      this.statusService.getStatusesWithEditPermForRoles(this.authService.user.roles).subscribe(statuses => {
        this.offerLifeCycleStatus = statuses;
      })
    );
    this.subscriptions.add(
      this.translate.onLangChange.subscribe(() => {
        this.tagChipListEl.focus();
      })
    );
  }

  add(tag: string): void {
    if (!tag) {
      return;
    }
    this.customTags.push(tag);
    this.filePondOption = this.getUploadConfig();
  }

  remove(index: number): void {
    this.customTags.splice(index, 1);
    this.filePondOption = this.getUploadConfig();
  }

  getApiError(res): void {
    if (res.statusCode === 500) {
      this.toastr.error(
        this.translate.instant('OFFER_UPLOAD.COMPONENT.ERROR'),
        this.translate.instant('OFFER_UPLOAD.COMPONENT.ERROR_TITLE')
      );
      return;
    }
    try {
      this.body = JSON.parse(res);
    } catch (err) {
      return;
    }
    if (this.body.errors) {
      this.offerService.csvValidationInfo$.next(this.body.errors);
    }
  }

  getApiMessage(res): void {
    try {
      this.body = JSON.parse(res);
    } catch (err) {
      return;
    }
    if (this.body.message) {
      this.toastr.success(`${this.body.message}`);
    }
  }

  navigateToWarningList(): void {
    if (!this.body.error) {
      this.router.navigate(['csv-upload-errors']);
    }
  }

  refreshErrorList(): void {
    this.offerService.csvValidationInfo$.next([]);
  }

  processFiles(): void {
    this.myPond.processFile();
  }

  getBody(): string {
    let body = {};
    const file = this.myPond ? this.myPond.getFile() : null;
    if (file) {
      body = { file: { base64: file.getFileEncodeBase64String(), name: file.filename } };
    }
    return JSON.stringify(body);
  }

  getPlaceholder(): string {
    return this.translate.instant('OFFER_UPLOAD.CUSTOM_LABELS');
  }

  getUploadConfig(): object {
    const config = {
      class: 'my-filepond',
      name: 'file',
      multiple: false,
      acceptedFileTypes: ['text/csv', 'application/vnd.ms-excel'],
      maxFileSize: '10MB',
      labelIdle: this.translate.instant('OFFER_UPLOAD.COMPONENT.POND_LABELS.DROP_FILE_HERE'),
      labelFileProcessing: this.translate.instant('OFFER_UPLOAD.COMPONENT.POND_LABELS.PROCESSING'),
      labelFileProcessingComplete: this.translate.instant('OFFER_UPLOAD.COMPONENT.POND_LABELS.PROCESSING_COMPLETE'),
      labelFileProcessingAborted: this.translate.instant('OFFER_UPLOAD.COMPONENT.POND_LABELS.PROCESSING_ABORTED'),
      labelFileProcessingError: this.translate.instant('OFFER_UPLOAD.COMPONENT.POND_LABELS.FILE_PROCESSING_ERROR'),
      labelTapToCancel: this.translate.instant('OFFER_UPLOAD.COMPONENT.POND_LABELS.TAP_TO_CANCEL'),
      labelTapToUndo: this.translate.instant('OFFER_UPLOAD.COMPONENT.POND_LABELS.TAP_TO_UNDO'),
      labelTapToRetry: this.translate.instant('OFFER_UPLOAD.COMPONENT.POND_LABELS.TAP_TO_RETRY'),
      labelFileTypeNotAllowed: this.translate.instant('OFFER_UPLOAD.COMPONENT.POND_LABELS.INVALID_TYPE'),
      labelMaxFileSize: this.translate.instant('OFFER_UPLOAD.COMPONENT.POND_LABELS.MAX_FILE_SIZE'),
      labelMaxFileSizeExceeded: this.translate.instant('OFFER_UPLOAD.COMPONENT.POND_LABELS.FILE_TO_BIG'),
      fileValidateTypeLabelExpectedTypes: this.translate.instant('OFFER_UPLOAD.COMPONENT.POND_LABELS.EXPECT'),
      allowFileEncode: true,
      server: {
        url: environment.OFFERS_API_URL,
        onload: res => this.getApiMessage(res),
        withCredentials: true,
        onerror: res => this.getApiError(res),
        process: (fieldName, file, metadata, load, error, progress, abort, transfer, options) => {
          const request = new XMLHttpRequest();
          const url = `${environment.OFFERS_API_URL}/file?decoding=${this.decoding ? 1 : 0}&dryRun=${
            this.dryRun ? 1 : 0
          }&offerStatus=${this.statusCode}&customTags=${encodeURIComponent(JSON.stringify(this.customTags))}`;

          request.open('POST', url);
          request.withCredentials = true;
          request.setRequestHeader('Content-type', 'application/json');
          request.setRequestHeader('Authorization', this.authService.token);
          request.setRequestHeader('X-XSRF-TOKEN', this.cookieService.get('XSRF-TOKEN'));

          request.upload.onprogress = e => {
            progress(e.lengthComputable, e.loaded, e.total);
          };

          request.onload = () => {
            if (request.status >= 200 && request.status < 300) {
              this.getApiMessage(request.responseText);
              load();
            } else {
              this.getApiError(request.responseText);
              error();
            }
          };

          request.send(this.getBody());

          return {
            abort: () => {
              request.abort();
              abort();
            }
          };
        }
      }
    };
    if (this.authService.featureTogglesFlags['FEAT_610']) {
      config['instantUpload'] = false;
    }
    return config;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  isButtonDisabled(): boolean {
    return !this.statusCode || !this.fileIsLoaded;
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/offers/csv-import/offer-upload/offer-upload.component.ts